import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Card from "@mui/material/Card";
import { mkConfig, generateCsv, download } from "export-to-csv";
import { Row, Col, Container } from "react-bootstrap";
import EditIcon from "@mui/icons-material/Edit";
import {
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import {
  MaterialReactTable,
  createMRTColumnHelper,
  useMaterialReactTable,
} from "material-react-table";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { ContentCopy, Delete as DeleteIcon } from "@mui/icons-material";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import axiosInstance from "../../utils/axiosInstance";

const EmployeeRole = () => {
  const roleResponse = useSelector((state) => state.login.login.role);

  const token = localStorage.getItem("token");

  const baseUrl = import.meta.env.VITE_BASE_URL;
  const [loading, setLoading] = useState(true);
  const [roleList, setRoleList] = useState([]);
  const [dataList, setDataList] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobileNo: "",
    designation: "",
    role: "",
  });
  const [errors, setErrors] = useState({});
  const [editingEmployeeId, setEditingEmployeeId] = useState(null); // Track if you're editing

  useEffect(() => {
    fetchEmployee();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: String(value),
    }));
  };

  const columnHelper = createMRTColumnHelper();

  const columns = [
    columnHelper.accessor("_id", {
      header: "ID",
      size: 30,
      Cell: ({ cell }) => {
        const handleCopy = () => {
          navigator.clipboard.writeText(cell.row.original?._id);
          toast.success("copied to clipboard!");
        };
        return (
          <Tooltip title={cell.row.original?._id}>
            <IconButton onClick={handleCopy}>
              <ContentCopy />
            </IconButton>
          </Tooltip>
        );
      },
    }),
    columnHelper.accessor("role", {
      header: "Role Id",
      size: 30,
      Cell: ({ cell }) => {
        const handleCopy = () => {
          navigator.clipboard.writeText(cell.row.original?.role);
          toast.success("copied to clipboard!");
        };

        return (
          <Tooltip title={cell.row.original?.role}>
            <IconButton onClick={handleCopy}>
              <ContentCopy />
            </IconButton>
          </Tooltip>
        );
      },
    }),
    // columnHelper.accessor("role", { header: "Role", size: 40 }),
    columnHelper.accessor("designation", { header: "Designation", size: 40 }),
    columnHelper.accessor("name", { header: "Name", size: 30 }),
    columnHelper.accessor("email", { header: "Email", size: 40 }),
    columnHelper.accessor("mobileNo", { header: "Mobile No", size: 40 }),
  ];

  const csvConfig = mkConfig({
    fieldSeparator: ",",
    decimalSeparator: ".",
    useKeysAsHeaders: true,
  });

  const handleExportData = () => {
    const rowData = dataList.map((employee) => ({
      name: employee.name,
      email: employee.Email,
      phoneNo: employee.PhoneNo,
      designation: employee.Designation,
    }));
    const csv = generateCsv(csvConfig)(rowData);
    download(csvConfig)(csv);
  };

  const handleExportRows = (rows) => {
    const rowData = rows.map((row) => ({
      name: row.original.name,
      email: row.original.Email,
      phoneNo: row.original.PhoneNo,
      designation: row.original.Designation,
    }));
    const csv = generateCsv(csvConfig)(rowData);
    download(csvConfig)(csv);
  };

  const table = useMaterialReactTable({
    columns,
    data: dataList,
    enableRowSelection: true,
    enableGlobalFilter: true,
    columnFilterDisplayMode: "popover",
    enableColumnActions: false,
    enableColumnFilters: false,
    enableSorting: true,
    enableDensityToggle: false,
    enableHiding: false,
    maxSize: 100,
    enableRowActions: true,
    positionActionsColumn: "last",
    muiTableHeadCellProps: {
      sx: (theme) => ({
        color: "#4a2a7d",
        background: "#DCDAFF",
        fontWeight: "normal",
        fontSize: "10px",
      }),
    },
    renderRowActions: ({ row }) => [
      <Box sx={{ display: "flex" }}>
        <>
        {roleResponse?.AdminEmployee?.update === 1 && (
          <IconButton onClick={() => updateEmployee(row.original)}>
            <EditIcon color="primary" />
          </IconButton>
        )}
        {roleResponse?.AdminEmployee?.delete === 1 && (
          <IconButton onClick={() => deleteEmployee(row.original._id)}>
            <DeleteIcon color="error" />
          </IconButton>
        )}
        </>
      </Box>,
    ],

    positionToolbarAlertBanner: "bottom",
    paginationDisplayMode: "pages",

    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
        }}
      >
        <Button
          onClick={() => handleExportData(table.getRowModel().rows)}
          startIcon={<FileDownloadIcon />}
        >
          Export All Data
        </Button>

        <Button
          disabled={
            !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
          }
          onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
          startIcon={<FileDownloadIcon />}
        >
          Export Selected Rows
        </Button>
      </Box>
    ),
  });

  useEffect(() => {
    fetchRole();
  }, []);

  const fetchEmployee = async () => {
    try {
      const url = `/employee_list`;
      // const url = `${baseUrl}/employee_list`;
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      };
      const response = await axiosInstance.get(url);
      // const response = await axios.get(url, { headers });
      setDataList(response.data.data);
      setLoading(false);
    } catch (error) {}
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.mobileNo.trim()) {
      newErrors.mobileNo = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formData.mobileNo.trim())) {
      newErrors.mobileNo = "Mobile number should be 10 digits";
    }

    if (!formData.role.trim()) newErrors.role = "Role is Required";

    if (!formData.designation.trim())
      newErrors.designation = "Designation is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const CreateNewRole = async () => {
    try {
      const url = `/employee_create`;
      // const url = `${baseUrl}/employee_create`;
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      };

      const response = await axiosInstance.post(url, formData);
      // const response = await axios.post(url, formData, { headers });

      if (response.data.error == false) {
        setFormData({
          name: "",
          email: "",
          mobileNo: "",
          designation: "",
          role: "",
        });

        toast.success(
          response?.data?.message || "Employee created sucessfully"
        );
        fetchEmployee();
      } else {
        toast.success(response?.data?.message);
      }
    } catch (error) {}
  };

  const handleCreateEmployee = () => {
    if (validateForm()) {
      if (editingEmployeeId) {
        updateExistingEmployee(editingEmployeeId);
      } else {
        CreateNewRole();
      }
    }
  };

  const updateEmployee = (employee) => {
    const selectedRole = roleList.find((role) => role._id === employee.role);
    setFormData({
      name: employee.name || "",
      email: employee.email || "",
      mobileNo: String(employee.mobileNo) || "",
      designation: employee.designation || "",
      role: selectedRole._id || "",
    });
    setEditingEmployeeId(employee._id);
    window.scrollTo({
      top: 0,
      behavior: "smooth", // for smooth scrolling
    });
  };

  const updateExistingEmployee = async (empId) => {
    try {
      const url = `/employee_update/${empId}`;
      // const url = `${baseUrl}/employee_update/${empId}`;
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      };

      const response = await axiosInstance.post(url, formData);
      // const response = await axios.post(url, formData, { headers });

      if (response.data.error === false) {
        toast.success(response?.data?.message || "");
        fetchEmployee();
        setFormData({
          name: "",
          email: "",
          mobileNo: "",
          designation: "",
          role: "",
        });
        setEditingEmployeeId(null);
      } else {
        toast.error("Failed to update employee");
      }
    } catch (error) {
      toast.error("Error updating employee");
    }
  };

  const fetchRole = async () => {
    try {
      const url = `/role_list`;
      // const url = `${baseUrl}/role_list`;
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      };
      const response = await axiosInstance.get(url);
      // const response = await axios.get(url, { headers });

      const list = response.data.data;
      setRoleList(list);
    } catch (error) {}
  };

  const deleteEmployee = async (empId) => {
    try {
      const confirmed = await Swal.fire({
        title: "Are you sure?",
        text: "Do you want to delete this employee?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (confirmed.isConfirmed) {
        const url = `/employee_delete/${empId}`;
        // const url = `${baseUrl}/employee_delete/${empId}`;
        const headers = {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        };

        const response = await axiosInstance.delete(url);
        // const response = await axios.delete(url, { headers });
        if (response.data.error === false) {
          fetchEmployee();
          Swal.fire("Deleted!", response.data.message, "success");
        }
      }
    } catch (error) {}
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="dashboard section">
          <ToastContainer />
          {roleResponse?.AdminEmployee?.write === 1  && (
            <Card className="mb-4" style={{ width: "100%" }}>
              <h2 className="TABLE_HEADING">ADD EMPLOYEE ROLE</h2>
              <Container fluid>
                <Row className="mb-4">
                  <Col xs={12}>
                    <Box
                      component="form"
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        margin: "0.5rem",
                      }}
                      noValidate
                      autoComplete="off"
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6} lg={3}>
                          <FormControl
                            sx={{ marginBottom: 1 }}
                            fullWidth
                            size="small"
                            variant="outlined"
                            error={!!errors.fetchError}
                          >
                            <InputLabel id="user-label">
                              {loading ? "Loading..." : "Select Role"}
                            </InputLabel>
                            <Select
                              labelId="employee-label"
                              id="role"
                              name="role"
                              value={formData.role} // Ensure this is the _id of the role
                              label="Select Role"
                              onChange={handleInputChange}
                              disabled={loading}
                              required
                            >
                              {roleList.map((role) => (
                                <MenuItem key={role._id} value={role._id}>
                                  {role.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.fetchError && (
                              <FormHelperText>
                                {errors.fetchError}
                              </FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                          <TextField
                            sx={{ marginBottom: 1 }}
                            id="name"
                            name="name"
                            label="Name"
                            required
                            fullWidth
                            size="small"
                            variant="outlined"
                            type="text"
                            value={formData.name}
                            onChange={handleInputChange}
                            error={!!errors.name}
                            helperText={errors.name}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                          <TextField
                            sx={{ marginBottom: 1 }}
                            id="email"
                            name="email"
                            label="email"
                            required
                            fullWidth
                            size="small"
                            variant="outlined"
                            type="text"
                            value={formData.email}
                            onChange={handleInputChange}
                            error={!!errors.email}
                            helperText={errors.email}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                          <TextField
                            sx={{ marginBottom: 1 }}
                            id="mobileNo"
                            name="mobileNo"
                            label="Mobile No."
                            required
                            fullWidth
                            size="small"
                            inputProps={{ maxLength: 10 }}
                            variant="outlined"
                            type="text"
                            value={formData.mobileNo}
                            onChange={handleInputChange}
                            error={!!errors.mobileNo}
                            helperText={errors.mobileNo}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                          <TextField
                            sx={{ marginBottom: 1 }}
                            id="designation"
                            name="designation"
                            label="designation"
                            required
                            fullWidth
                            size="small"
                            variant="outlined"
                            type="text"
                            // inputProps={{ maxLength: 15 }}
                            value={formData.designation}
                            onChange={handleInputChange}
                            error={!!errors.designation}
                            helperText={errors.designation}
                          />
                        </Grid>
                        {editingEmployeeId && (
                          <Grid item xs={12} md={6} lg={3}>
                            <Button
                              variant="contained"
                              // onClick={handleCreateEmployee}
                              onClick={() => {
                                setFormData({
                                  name: "",
                                  email: "",
                                  mobileNo: "",
                                  designation: "",
                                  role: "",
                                });
                                setEditingEmployeeId(null);
                              }}
                              className="button-width"
                              style={{ padding: "8px" }}
                            >
                              {/* {editingEmployeeId ? "Update" : "Create"} */}
                              cancel
                            </Button>
                          </Grid>
                        )}
                        <Grid item xs={12} md={6} lg={3}>
                          <Button
                            variant="contained"
                            onClick={handleCreateEmployee}
                            className="button-width"
                            style={{ padding: "8px" }}
                          >
                            {editingEmployeeId ? "Update" : "Create"}
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Col>
                </Row>
              </Container>
            </Card>
          )}
          <MaterialReactTable table={table} />
        </div>
      </div>
    </div>
  );
};

export default EmployeeRole;
